import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Slider,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CropIcon from "@mui/icons-material/Crop";
import TuneIcon from "@mui/icons-material/Tune";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";

const STEPS = ["Subir imagen", "Ajustar umbral", "Recortar", "Vista previa"];

// ─── Otsu's Method ────────────────────────────────────────────────
function calculateOtsuThreshold(imageData) {
  const histogram = new Array(256).fill(0);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(
      0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    );
    histogram[gray]++;
  }

  const totalPixels = data.length / 4;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * histogram[i];

  let sumB = 0,
    wB = 0,
    wF = 0,
    maxVariance = 0,
    threshold = 128;

  for (let t = 0; t < 256; t++) {
    wB += histogram[t];
    if (wB === 0) continue;
    wF = totalPixels - wB;
    if (wF === 0) break;

    sumB += t * histogram[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const variance = wB * wF * (mB - mF) * (mB - mF);

    if (variance > maxVariance) {
      maxVariance = variance;
      threshold = t;
    }
  }

  return threshold;
}

// ─── Canvas Processing ────────────────────────────────────────────
function processImageWithThreshold(image, threshold) {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (gray < threshold) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 255;
    } else {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}

function getOtsuFromImage(image) {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return calculateOtsuThreshold(imageData);
}

// ─── Crop Extraction ──────────────────────────────────────────────
// Handles crop regions that extend beyond image boundaries (common with
// objectFit="contain" and zoom < 1). Computes the intersection between
// the crop area and the actual image, draws only that portion, and
// leaves the rest transparent.
function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const imgW = image.naturalWidth || image.width;
      const imgH = image.naturalHeight || image.height;

      // Intersection of crop rect with image rect
      const sx = Math.max(0, pixelCrop.x);
      const sy = Math.max(0, pixelCrop.y);
      const sx2 = Math.min(imgW, pixelCrop.x + pixelCrop.width);
      const sy2 = Math.min(imgH, pixelCrop.y + pixelCrop.height);
      const sw = Math.max(0, sx2 - sx);
      const sh = Math.max(0, sy2 - sy);

      // Where that intersection lands on the output canvas
      const dx = sx - pixelCrop.x;
      const dy = sy - pixelCrop.y;

      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");
      // Canvas is transparent by default — only draw the valid region
      if (sw > 0 && sh > 0) {
        ctx.drawImage(image, sx, sy, sw, sh, dx, dy, sw, sh);
      }
      resolve(canvas.toDataURL("image/png"));
    };
    image.src = imageSrc;
  });
}

// ─── Checkered Background CSS ─────────────────────────────────────
const checkeredBg = {
  backgroundImage:
    "repeating-conic-gradient(#d0d0d0 0% 25%, #f5f5f5 0% 50%)",
  backgroundSize: "16px 16px",
};

// ─── Step 1: Upload ───────────────────────────────────────────────
const StepUpload = ({ onImageSelected }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(
    (file) => {
      if (!file || !file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
        onImageSelected(e.target.result);
      };
      reader.readAsDataURL(file);
    },
    [onImageSelected]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      handleFile(file);
    },
    [handleFile]
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Sube una imagen con tu firma manuscrita. Formatos aceptados: JPG, PNG,
        etc.
      </Typography>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "grey.300",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: dragOver ? "primary.50" : "grey.50",
          transition: "all 0.2s",
          "&:hover": { borderColor: "primary.light", bgcolor: "primary.50" },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <CloudUploadIcon
          sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
        />
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Arrastra tu imagen aquí o haz clic para seleccionar
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Subir imagen
        </Typography>
      </Box>

      {preview && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", mb: 1, display: "block" }}
          >
            Vista previa de la imagen cargada
          </Typography>
          <Box
            component="img"
            src={preview}
            alt="Vista previa"
            sx={{
              maxWidth: "100%",
              maxHeight: 200,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Step 2: Threshold ────────────────────────────────────────────
const StepThreshold = ({ threshold, setThreshold, processedImage }) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Ajusta el control hasta que tu firma sea claramente visible contra el
        fondo transparente. El cuadriculado indica las zonas transparentes.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TuneIcon sx={{ color: "text.secondary" }} />
        <Slider
          value={threshold}
          onChange={(_, val) => setThreshold(val)}
          min={30}
          max={230}
          valueLabelDisplay="auto"
          sx={{ flex: 1 }}
        />
        <Typography
          variant="body2"
          sx={{ minWidth: 32, textAlign: "right", fontWeight: 600 }}
        >
          {threshold}
        </Typography>
      </Box>

      {processedImage && (
        <Box
          sx={{
            ...checkeredBg,
            borderRadius: 2,
            p: 2,
            display: "flex",
            justifyContent: "center",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Box
            component="img"
            src={processedImage}
            alt="Firma procesada"
            sx={{ maxWidth: "100%", maxHeight: 250 }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Step 3: Crop ─────────────────────────────────────────────────
const StepCrop = ({
  processedImage,
  crop,
  setCrop,
  zoom,
  setZoom,
  onCropComplete,
}) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Posiciona y ajusta el zoom para encuadrar tu firma dentro del marco 3:1.
      </Typography>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 280,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "grey.200",
          ...checkeredBg,
        }}
      >
        <Cropper
          image={processedImage}
          crop={crop}
          zoom={zoom}
          minZoom={0.05}
          aspect={3 / 1}
          objectFit="contain"
          restrictPosition={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={(_, croppedAreaPixels) =>
            onCropComplete(croppedAreaPixels)
          }
          style={{
            containerStyle: { borderRadius: 8 },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Zoom
        </Typography>
        <Slider
          value={zoom}
          onChange={(_, val) => setZoom(val)}
          min={0.05}
          max={5}
          step={0.1}
          sx={{ flex: 1 }}
        />
      </Box>
    </Box>
  );
};

// ─── Step 4: Final Preview ────────────────────────────────────────
const StepPreview = ({ croppedImage, userName }) => {
  const today = new Date();
  const dateStr = today.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Revisa el resultado final de tu firma electrónica.
      </Typography>

      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}
        >
          Tu firma:
        </Typography>
        <Box
          sx={{
            display: "inline-block",
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 2,
            p: 2,
            ...checkeredBg,
          }}
        >
          <Box
            component="img"
            src={croppedImage}
            alt="Firma final"
            sx={{ maxWidth: "100%", height: 80 }}
          />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mt: 1.5, textTransform: "uppercase" }}
        >
          {userName}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Firmado electrónicamente el {dateStr}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const SignatureUpload = ({ userName, userId, onConfirm }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [existingUrl, setExistingUrl] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Check if a signature already exists in Storage
  useEffect(() => {
    if (!userId) {
      setLoadingExisting(false);
      return;
    }
    const storageRef = ref(storage, `/files/${userId}/signature.png`);
    getDownloadURL(storageRef)
      .then((url) => {
        setExistingUrl(url);
        setEditing(false);
      })
      .catch(() => {
        setExistingUrl(null);
        setEditing(true);
      })
      .finally(() => setLoadingExisting(false));
  }, [userId]);

  // Step 1 state
  const [rawImage, setRawImage] = useState(null);
  const [imageEl, setImageEl] = useState(null);

  // Step 2 state
  const [threshold, setThreshold] = useState(128);
  const [processedImage, setProcessedImage] = useState(null);

  // Step 3 state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.5);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Step 4 state
  const [croppedImage, setCroppedImage] = useState(null);

  // Load image element when rawImage changes
  useEffect(() => {
    if (!rawImage) return;
    const img = new Image();
    img.onload = () => {
      setImageEl(img);
      const otsu = getOtsuFromImage(img);
      setThreshold(otsu);
    };
    img.src = rawImage;
  }, [rawImage]);

  // Reprocess when threshold or imageEl changes
  useEffect(() => {
    if (!imageEl) return;
    const result = processImageWithThreshold(imageEl, threshold);
    setProcessedImage(result);
  }, [imageEl, threshold]);

  const handleImageSelected = useCallback((dataUrl) => {
    setRawImage(dataUrl);
  }, []);

  const handleNext = useCallback(async () => {
    if (activeStep === 2 && processedImage && croppedAreaPixels) {
      const cropped = await getCroppedImg(processedImage, croppedAreaPixels);
      setCroppedImage(cropped);
    }
    setActiveStep((prev) => prev + 1);
  }, [activeStep, processedImage, croppedAreaPixels]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleRestart = useCallback(() => {
    setActiveStep(0);
    setRawImage(null);
    setImageEl(null);
    setProcessedImage(null);
    setCroppedImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(0.5);
    setCroppedAreaPixels(null);
    setThreshold(128);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!croppedImage || !userId) return;
    setUploading(true);
    try {
      const blob = await fetch(croppedImage).then((r) => r.blob());
      const storageRef = ref(
        storage,
        `/files/${userId}/signature.png`
      );
      await uploadBytes(storageRef, blob, { contentType: "image/png" });
      const downloadURL = await getDownloadURL(storageRef);
      setExistingUrl(downloadURL);
      if (onConfirm) onConfirm(downloadURL);
      setEditing(false);
      handleRestart();
    } catch (error) {
      debugLog("Error uploading signature:", error);
    } finally {
      setUploading(false);
    }
  }, [croppedImage, userId, onConfirm, handleRestart]);

  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return !!rawImage;
      case 1:
        return !!processedImage;
      case 2:
        return !!croppedAreaPixels;
      case 3:
        return !!croppedImage;
      default:
        return false;
    }
  }, [activeStep, rawImage, processedImage, croppedAreaPixels, croppedImage]);

  // Loading state while checking for existing signature
  if (loadingExisting) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "grey.200",
          textAlign: "center",
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    );
  }

  // Show existing signature when not editing
  if (!editing && existingUrl) {
    const today = new Date();
    const dateStr = today.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            fontSize: { xs: "1rem", sm: "1.15rem" },
            color: "text.primary",
          }}
        >
          Firma electrónica
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 3, fontSize: "0.8rem" }}
        >
          Tu firma actual registrada en el sistema
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-block",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 2,
              p: 2,
              ...checkeredBg,
            }}
          >
            <Box
              component="img"
              src={existingUrl}
              alt="Firma actual"
              sx={{ maxWidth: "100%", height: 80 }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mt: 1.5, textTransform: "uppercase" }}
          >
            {userName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 2 }}
          >
            Firmado electrónicamente el {dateStr}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={() => setEditing(true)}
            size="small"
          >
            Cambiar firma
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: "1rem", sm: "1.15rem" },
          color: "text.primary",
        }}
      >
        Firma electrónica
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 3, fontSize: "0.8rem" }}
      >
        Sube y procesa tu firma manuscrita para uso en documentos
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 300 }}>
        {activeStep === 0 && (
          <StepUpload onImageSelected={handleImageSelected} />
        )}
        {activeStep === 1 && (
          <StepThreshold
            threshold={threshold}
            setThreshold={setThreshold}
            processedImage={processedImage}
          />
        )}
        {activeStep === 2 && processedImage && (
          <StepCrop
            processedImage={processedImage}
            crop={crop}
            setCrop={setCrop}
            zoom={zoom}
            setZoom={setZoom}
            onCropComplete={setCroppedAreaPixels}
          />
        )}
        {activeStep === 3 && (
          <StepPreview croppedImage={croppedImage} userName={userName} />
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "grey.100",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={uploading}
              size="small"
            >
              Atrás
            </Button>
          )}
          {activeStep > 0 && (
            <IconButton
              onClick={handleRestart}
              disabled={uploading}
              size="small"
              title="Volver a subir"
              sx={{ color: "text.secondary" }}
            >
              <RestartAltIcon />
            </IconButton>
          )}
          {existingUrl && (
            <Button
              variant="text"
              onClick={() => {
                setEditing(false);
                handleRestart();
              }}
              disabled={uploading}
              size="small"
            >
              Cancelar
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep === 3 && (
            <>
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => setActiveStep(1)}
                disabled={uploading}
                size="small"
              >
                Re-ajustar
              </Button>
              <Button
                variant="outlined"
                startIcon={<CropIcon />}
                onClick={() => setActiveStep(2)}
                disabled={uploading}
                size="small"
              >
                Recortar
              </Button>
            </>
          )}

          {activeStep < 3 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed}
              size="small"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CheckCircleOutlineIcon />
                )
              }
              onClick={handleConfirm}
              disabled={!croppedImage || uploading}
              size="small"
            >
              {uploading ? "Subiendo..." : "Confirmar firma"}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default SignatureUpload;
